import { motion } from 'framer-motion'
import { Link, Routes, Route, Navigate, useParams } from 'react-router-dom'
import { ArrowRight, CheckCircle2, ChevronLeft, Handshake, Workflow } from 'lucide-react'
import { engagements, contactHref } from '../content/services'

function ServicesIndex() {
  return (
    <div className="pt-24 pb-16">
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-synth-900/50 border border-synth-700/50 text-synth-300 text-sm font-medium mb-6">
              <Handshake className="w-4 h-4" />
              Services
            </span>
            <h1 className="text-5xl md:text-6xl font-display font-bold text-void-50 mb-6">
              Adopt it with the <span className="text-gradient">people who built it</span>
            </h1>
            <p className="text-xl text-void-400 max-w-2xl mx-auto">
              Training, a guided adoption program, and managed operations for teams making Graffiti™
              and 23Controls™ their core system for secure, local RAG and DAG operations.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6">
          {engagements.map((e, i) => (
            <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Link
                to={`/services/${e.id}`}
                className="group flex flex-col h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${e.gradient} mb-6 w-fit`}>
                  <e.icon className="w-7 h-7 text-white" />
                </div>
                <h2 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">{e.name}</h2>
                <p className="text-void-400 mb-6 flex-1">{e.tagline}</p>
                <span className="inline-flex items-center gap-2 text-synth-400 font-medium group-hover:gap-3 transition-all">
                  Learn more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <ServicesCTA />
    </div>
  )
}

function ServicesCTA() {
  return (
    <section className="py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="p-8 md:p-10 rounded-3xl bg-gradient-to-br from-synth-900/50 to-void-900 border border-synth-800/30 text-center">
          <h2 className="text-2xl md:text-3xl font-display font-bold text-void-50 mb-3">Every engagement starts with a conversation</h2>
          <p className="text-void-400 mb-6 max-w-2xl mx-auto">
            We're a small team. Tell us what you're trying to run and we'll tell you honestly what's ready
            for it, what's in pilot, and how we'd approach it.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to={contactHref} className="btn-primary inline-flex items-center gap-2">
              Talk to our team <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/ai-operations" className="btn-secondary inline-flex items-center gap-2">
              <Workflow className="w-4 h-4" /> See the AI Operations Suite
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

function EngagementPage() {
  const { engagementId } = useParams()
  const e = engagements.find((x) => x.id === engagementId)
  if (!e) return <Navigate to="/services" replace />

  return (
    <div className="pt-24 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/services" className="inline-flex items-center gap-2 text-void-400 hover:text-void-200 transition-colors mb-8">
          <ChevronLeft className="w-4 h-4" />
          All services
        </Link>

        <motion.div key={e.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${e.gradient} mb-6`}>
            <e.icon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">{e.name}</h1>
          <p className="text-xl text-void-300 mb-4 max-w-3xl">{e.tagline}</p>
          <p className="text-void-400 mb-12 max-w-3xl"><span className="text-void-200">Who it's for:</span> {e.audience}</p>

          <h2 className="text-xl font-display font-bold text-void-50 mb-4">What's included</h2>
          <ul className="space-y-2 mb-12">
            {e.includes.map((item) => (
              <li key={item} className="flex items-start gap-2 text-void-300 text-sm">
                <CheckCircle2 className="w-4 h-4 text-synth-500 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>

          <h2 className="text-xl font-display font-bold text-void-50 mb-4">How it works</h2>
          <div className={`grid sm:grid-cols-2 ${e.phases.length > 3 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4 mb-8`}>
            {e.phases.map((p, i) => (
              <div key={p.label} className="p-5 rounded-xl bg-void-900/30 border border-void-800/50">
                <span className="font-mono text-xs text-synth-400">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-display font-semibold text-void-100 mt-1 mb-1">{p.label}</h3>
                <p className="text-void-400 text-sm leading-relaxed">{p.detail}</p>
              </div>
            ))}
          </div>

          <p className="text-void-500 text-sm max-w-3xl mb-4">{e.note}</p>
        </motion.div>
      </div>

      <ServicesCTA />
    </div>
  )
}

export default function ServicesPage() {
  return (
    <Routes>
      <Route index element={<ServicesIndex />} />
      <Route path=":engagementId" element={<EngagementPage />} />
    </Routes>
  )
}
